<template>
  <div class="app" @mousedown="onMousedownApp($event)">
    <MainMenu />
    <RouterView class="view" />
    <Notifications ref="notifications" />
    <ImportStack />
    <ModalUpdateAccount />
    <ModalUpdateMember />
    <ModalUpdateRole />
    <ModalUpdateHost />
    <ModalUpdateProvider />
    <ModalUpdateProject />
    <ModalUpdateService />
    <ModalMoveContainer />
    <ModalScaleService />
    <ModalCreateAccount />
    <ModalCreateMember />
    <ModalCreateRole />
    <ModalCreateProject />
    <ModalCreateService />
    <ModalCreateHost />
    <ModalCreateProvider />
    <ModalCreateKey />
    <ExecuteCommands />
    <ModalTerminal />
    <ModalLog />
    <ModalMetrics />
    <ModalBackup />
    <ModalAccess name="entityAccess" />
    <ModalAccess name="globalAccess" />
    <ModalConfirm />
    <ModalSupport />
    <TrackerCarrotQuest />
    <TrackerFacebookPixel />
    <TrackerYandexMetrika />
    <Dev v-once v-if="config.debug" />
  </div>
</template>

<script type="text/javascript">
  import {mapGetters} from 'vuex'
  import MainMenu from './components/MainMenu'
  import ExecuteCommands from './components/modals/ExecuteCommands'
  import ImportStack from './components/modals/ImportStack'
  import ModalAccess from './components/modals/Access'
  import ModalBackup from './components/modals/Backup'
  import ModalConfirm from './components/modals/Confirm'
  import ModalSupport from './components/modals/Support'
  import ModalCreateHost from './components/modals/CreateHost'
  import ModalCreateAccount from './components/modals/CreateAccount'
  import ModalCreateMember from './components/modals/CreateMember'
  import ModalCreateRole from './components/modals/CreateRole'
  import ModalCreateKey from './components/modals/CreateKey'
  import ModalCreateProject from './components/modals/CreateProject'
  import ModalCreateProvider from './components/modals/CreateProvider'
  import ModalCreateService from './components/modals/CreateService'
  import ModalLog from './components/modals/Log'
  import ModalMetrics from './components/modals/Metrics'
  import ModalMoveContainer from './components/modals/MoveContainer'
  import ModalScaleService from './components/modals/ScaleService'
  import ModalTerminal from './components/modals/Terminal'
  import ModalUpdateAccount from './components/modals/UpdateAccount'
  import ModalUpdateMember from './components/modals/UpdateMember'
  import ModalUpdateRole from './components/modals/UpdateRole'
  import ModalUpdateHost from './components/modals/UpdateHost'
  import ModalUpdateProvider from './components/modals/UpdateProvider'
  import ModalUpdateProject from './components/modals/UpdateProject'
  import ModalUpdateService from './components/modals/UpdateService'
  import Notifications from './components/Notifications'
  import TrackerCarrotQuest from './components/trackers/CarrotQuest'
  import TrackerFacebookPixel from './components/trackers/FacebookPixel'
  import TrackerYandexMetrika from './components/trackers/YandexMetrika'
  import Dev from '@/components/Dev'
  import api from '@/api'
  import logger from '@/logger'
  import utils from '@/utils'

  export default {
    name: 'App',
    components: {
      MainMenu,
      ExecuteCommands,
      ImportStack,
      ModalAccess,
      ModalBackup,
      ModalConfirm,
      ModalSupport,
      ModalCreateAccount,
      ModalCreateMember,
      ModalCreateRole,
      ModalCreateHost,
      ModalCreateKey,
      ModalCreateProject,
      ModalCreateProvider,
      ModalCreateService,
      ModalLog,
      ModalMetrics,
      ModalMoveContainer,
      ModalScaleService,
      ModalTerminal,
      ModalUpdateAccount,
      ModalUpdateMember,
      ModalUpdateRole,
      ModalUpdateHost,
      ModalUpdateProvider,
      ModalUpdateProject,
      ModalUpdateService,
      Notifications,
      TrackerCarrotQuest,
      TrackerFacebookPixel,
      TrackerYandexMetrika,
      Dev,
    },
    computed: {
      ...mapGetters(['config', 'hostsById', 'providersById']),
      hostIds() {
        return Object.keys(this.hostsById)
      },
    },
    watch: {
      hostIds: {
        immediate: true,
        handler(hostIds) {
          for (const hostId of hostIds) {
            api.subscriptions.hostStats[hostId] = 1
          }
          api.subscriptions.updateHostStats()
        },
      },
      'config.maintenance': {
        immediate: true,
        handler() {
          if (this.config.maintenance) {
            if (this.$route.path !== '/error/maintenance') {
              this.$router.push(`/error/maintenance?redirect=${this.$route.path}`)
            }
          } else {
            if (this.$route.path === '/error/maintenance') {
              this.$router.push(this.$route.query.redirect || '/').catch(() => {})
            }
          }
        },
      },
      'config.checkingUpdates': {
        immediate: true,
        handler() {
          if (this.config.checkingUpdates) {
            this.$options.checkingUpdates = setInterval(utils.checkUpdates, this.config.checkingUpdatesInterval)
          } else {
            clearInterval(this.$options.checkingUpdates)
          }
        },
      },
      'config.checkingUpdatesInterval'() {
        if (this.config.checkingUpdates) {
          clearInterval(this.$options.checkingUpdates)
          if (this.config.checkingUpdates) {
            this.$options.checkingUpdates = setInterval(utils.checkUpdates, this.config.checkingUpdatesInterval)
          }
        }
      },
      providersById(providersById) {
        for (const id in providersById) {
          if (providersById[id].expired) {
            logger.notify({
              entity: providersById[id],
              type: 'warning',
              text: 'Your token has been expired. Please, refresh it to continue using this provider.',
              buttons: [{
                text: 'Refresh',
                action: () => {
                  this.$modals.updateProvider.open(providersById[id])
                }
              }, {
                text: 'Later',
              }]
            })
          }
        }
      },
    },
    methods: {
      onMousedownApp(event) {
        const target = event.target && event.target.closest && event.target.closest('[data-goal]')
        if (target && target.dataset && target.dataset.goal) {
          utils.reachGoal(target.dataset.goal)
        }
      },
      closeModals(event) {
        if (event.keyCode === 27) {
          for (const modalName in this.$modals) {
            const modal = this.$modals[modalName]
            if (modal.visible && !modal.unescapable && !modal.hasChanges) {
              modal.close()
            }
          }
        }
      },
    },
    created() {
      window.addEventListener('keyup', this.closeModals)
    },
    beforeDestroy() {
      window.removeEventListener('keyup', this.closeModals)
    },
  }
</script>

<style scoped>
  .app { display: flex; flex-direction: column; height: 100%; }
  .view { flex: 1; }
</style>
